import { ChartConfig, ChartContainer } from "design-system/molecules/Chart"
import { ChartCard } from "design-system/organisms/insights/ChartCard"
import { cn } from "design-system/utils"
import { TrendingDown, TrendingUp } from "lucide-react"
import { useNPSQuery } from "modules/insights/hooks/useNPSQuery"
import { useTranslation } from "react-i18next"
import { Cell, Label, Pie, PieChart } from "recharts"

const mapNegativeNPS = (nps: number) => {
  return (nps + 100) * 0.5
}

const mapPositiveNPS = (nps: number) => {
  return 50 + nps * 0.5
}

const chartConfig = {} satisfies ChartConfig

type Props = {
  data: ReturnType<typeof useNPSQuery>["data"]
  isPending: boolean
}

export function NetPromoterScore({ data, isPending }: Props) {
  const { t } = useTranslation()
  const noData =
    !isPending && data?.data.score === undefined && data?.data.previousScore === undefined

  const nps = data?.data.score ? Math.min(Math.max(data.data.score, -100), 100) : 0
  const previousNps = data?.data.previousScore

  let trend: number | null = null
  if (previousNps) {
    trend =
      data?.data.score !== undefined
        ? Math.round(100 * ((100 + nps) / (100 + previousNps))) - 100
        : 0
  }

  const gaugeData = [
    { value: nps < 0 ? mapNegativeNPS(nps) : 0 }, // Negative
    { value: nps >= 0 && nps < 40 ? mapPositiveNPS(nps) : 0 }, // Neutral
    { value: nps >= 40 ? mapPositiveNPS(nps) : 0 }, // Positive
    { value: nps < 0 ? 100 - mapNegativeNPS(nps) : 100 - mapPositiveNPS(nps) }, // Background
  ]

  const chart = (
    <ChartContainer
      config={chartConfig}
      className={cn(
        "mx-auto aspect-square max-h-[250px]",
        noData && "pointer-events-none grayscale opacity-25 dark:opacity-5",
      )}
    >
      <PieChart>
        <Pie
          data={gaugeData}
          dataKey="value"
          startAngle={225}
          endAngle={-45}
          innerRadius={80}
          outerRadius={110}
          cornerRadius={0}
        >
          <Cell fill="var(--negative)" /> {/* Left active (negative) */}
          <Cell fill="var(--neutral)" /> {/* Right active (positive) */}
          <Cell fill="var(--positive)" /> {/* Right active (positive) */}
          <Cell fill="var(--muted)" /> {/* Right background */}
          <Label
            content={({ viewBox }) => {
              if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                return (
                  <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                    <tspan
                      x={viewBox.cx}
                      y={viewBox.cy}
                      className="fill-foreground text-4xl font-normal font-mono"
                    >
                      {noData ? "-" : nps}
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy || 0) + 30}
                      className="fill-muted-foreground"
                    >
                      {t("insights.nps.score.label")}
                    </tspan>
                  </text>
                )
              }
            }}
          />
        </Pie>
      </PieChart>
    </ChartContainer>
  )

  const footer = (
    <>
      <div className="flex items-center gap-2 font-medium leading-none">
        {!trend && <span>{t("insights.trend.noChange", { value: 0 })}</span>}
        {!!trend && trend > 0 && (
          <>
            <span>{t("insights.trend.up", { value: trend })}</span>
            <TrendingUp className="h-4 w-4 text-success" />
          </>
        )}
        {!!trend && trend < 0 && (
          <>
            <span>{t("insights.trend.down", { value: trend })}</span>
            <TrendingDown className="h-4 w-4 text-error" />
          </>
        )}
      </div>
      <div className="leading-none text-muted-foreground">
        {t("insights.timeRange.comparedToPreviousPeriod")}
      </div>
    </>
  )

  return (
    <ChartCard
      title={t("insights.nps.score.title")}
      description={t("insights.nps.score.description")}
      chart={chart}
      footer={footer}
      isPending={isPending}
      fixedWidth={true}
      noData={{
        show: noData,
        title: t("insights.nps.noData.title"),
        description: t("insights.nps.noData.description"),
      }}
      chartContainerClassName="min-h-[250px] max-h-[250px]"
    />
  )
}
